/**
 * useMessaging — Hook für WhatsApp & SMS via Twilio
 *
 * Features:
 * - Nachrichten laden (WhatsApp + SMS)
 * - Nachrichten senden
 * - Als gelesen markieren
 * - Realtime-Subscription für neue Nachrichten
 * - Conversations gruppiert nach Telefonnummer
 * - Twilio-Konfigurationsstatus
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface TwilioMessage {
  id: string;
  channel: 'whatsapp' | 'sms';
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  body: string | null;
  media_urls: string[] | null;
  status: string;
  twilio_sid: string | null;
  contact_name: string | null;
  contact_id: string | null;
  conversation_id: string | null;
  created_at: string;
  read_at: string | null;
  wa_message_type: string | null;
  wa_template_name: string | null;
  sent_by: string | null;
  error_code: string | null;
  error_message: string | null;
}

export interface MessagingConversation {
  conversationId: string;
  channel: 'whatsapp' | 'sms';
  phoneNumber: string;
  contactName: string | null;
  contactId: string | null;
  lastMessage: TwilioMessage;
  unreadCount: number;
  messages: TwilioMessage[];
}

export interface TwilioConfig {
  configured: boolean;
  whatsappNumber: string | null;
  smsNumber: string | null;
}

async function twilioApiFetch(body: object): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  return fetch('/api/twilio-api', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify(body),
  });
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useMessaging() {
  const { profile, impersonateUserId } = useAuth();
  const currentUserId = impersonateUserId || profile?.id;

  const [allMessages, setAllMessages] = useState<TwilioMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState<TwilioConfig>({
    configured: false, whatsappNumber: null, smsNumber: null,
  });
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const subscriptionRef = useRef<any>(null);

  // ─── Load messages ──────────────────────────────────────────────────────

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('twilio_messages')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) {
        console.error('[useMessaging] Load error:', error);
        // Table may not exist yet — gracefully handle
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          setAllMessages([]);
          return;
        }
        throw error;
      }

      setAllMessages(data || []);
    } catch (err) {
      console.error('[useMessaging] Error loading messages:', err);
      setAllMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Load config ────────────────────────────────────────────────────────

  const loadConfig = useCallback(async () => {
    try {
      const resp = await twilioApiFetch({ action: 'getConfig' });
      if (resp.ok) {
        const data = await resp.json();
        setConfig({
          configured: data.configured,
          whatsappNumber: data.config?.whatsapp_number || null,
          smsNumber: data.config?.sms_number || null,
        });
      }
    } catch {
      // API might not be deployed yet
    }
  }, []);

  // ─── Initial load ───────────────────────────────────────────────────────

  useEffect(() => {
    loadMessages();
    loadConfig();
  }, [loadMessages, loadConfig]);

  // ─── Realtime subscription ──────────────────────────────────────────────

  useEffect(() => {
    const channel = supabase
      .channel('twilio-messages-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'twilio_messages' },
        (payload) => {
          const newMsg = payload.new as TwilioMessage;
          setAllMessages(prev => [newMsg, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'twilio_messages' },
        (payload) => {
          const updated = payload.new as TwilioMessage;
          setAllMessages(prev => prev.map(m => m.id === updated.id ? updated : m));
        }
      )
      .subscribe();

    subscriptionRef.current = channel;

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, []);

  // ─── Group into conversations ───────────────────────────────────────────

  const conversations = useMemo((): MessagingConversation[] => {
    const convMap = new Map<string, TwilioMessage[]>();

    for (const msg of allMessages) {
      const convId = msg.conversation_id || `${msg.channel}-${(msg.direction === 'inbound' ? msg.from_number : msg.to_number).replace(/[^0-9]/g, '')}`;
      if (!convMap.has(convId)) convMap.set(convId, []);
      convMap.get(convId)!.push(msg);
    }

    const result: MessagingConversation[] = [];
    for (const [convId, msgs] of convMap) {
      // Sort messages chronologically (oldest first)
      msgs.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      const lastMsg = msgs[msgs.length - 1];
      const firstInbound = msgs.find(m => m.direction === 'inbound');
      const phoneNumber = firstInbound?.from_number || lastMsg.to_number;

      result.push({
        conversationId: convId,
        channel: lastMsg.channel,
        phoneNumber,
        contactName: firstInbound?.contact_name || lastMsg.contact_name,
        contactId: firstInbound?.contact_id || lastMsg.contact_id,
        lastMessage: lastMsg,
        unreadCount: msgs.filter(m => m.direction === 'inbound' && !m.read_at).length,
        messages: msgs,
      });
    }

    // Sort by last message time (newest first)
    result.sort((a, b) => new Date(b.lastMessage.created_at).getTime() - new Date(a.lastMessage.created_at).getTime());

    return result;
  }, [allMessages]);

  // ─── Filter by channel ─────────────────────────────────────────────────

  const whatsappConversations = useMemo(
    () => conversations.filter(c => c.channel === 'whatsapp'),
    [conversations]
  );

  const smsConversations = useMemo(
    () => conversations.filter(c => c.channel === 'sms'),
    [conversations]
  );

  // ─── Get messages for active conversation ───────────────────────────────

  const activeMessages = useMemo(
    () => conversations.find(c => c.conversationId === activeConversationId)?.messages || [],
    [conversations, activeConversationId]
  );

  // ─── Send message ──────────────────────────────────────────────────────

  const sendMessage = useCallback(async (
    channel: 'whatsapp' | 'sms',
    to: string,
    body: string,
    templateName?: string,
    templateParams?: Record<string, string>,
  ) => {
    setSending(true);
    try {
      const resp = await twilioApiFetch({
        action: 'sendMessage',
        channel,
        to,
        body,
        templateName,
        templateParams,
        sentBy: currentUserId,
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);

      // Optimistic update
      if (data.message) {
        setAllMessages(prev => [data.message, ...prev]);
      }

      return data;
    } finally {
      setSending(false);
    }
  }, [currentUserId]);

  // ─── Mark as read ──────────────────────────────────────────────────────

  const markConversationRead = useCallback(async (conversationId: string) => {
    try {
      // Optimistic update
      setAllMessages(prev => prev.map(m =>
        m.conversation_id === conversationId && !m.read_at
          ? { ...m, read_at: new Date().toISOString() }
          : m
      ));

      await twilioApiFetch({ action: 'markRead', conversationId });
    } catch (err) {
      console.error('[useMessaging] Mark read error:', err);
    }
  }, []);

  // ─── Open conversation ─────────────────────────────────────────────────

  const openConversation = useCallback((conversationId: string) => {
    setActiveConversationId(conversationId);
    markConversationRead(conversationId);
  }, [markConversationRead]);

  return {
    // State
    loading,
    sending,
    config,
    conversations,
    whatsappConversations,
    smsConversations,
    activeConversationId,
    activeMessages,

    // Actions
    sendMessage,
    markConversationRead,
    openConversation,
    setActiveConversationId,
    loadMessages,

    // Computed
    whatsappUnreadCount: whatsappConversations.reduce((sum, c) => sum + c.unreadCount, 0),
    smsUnreadCount: smsConversations.reduce((sum, c) => sum + c.unreadCount, 0),
  };
}
